/**
 * London Dynamics 3D configurator, shared by blocks/image-with-3d.liquid and
 * snippets/product-swiper-gallery-3d.liquid. The preconnect/modulepreload tags for the
 * same URLs live in snippets/london-dynamics-preload.liquid.
 */

const SCRIPT_URL = 'https://web-packages.londondynamics.com/webcomponent/v/latest/configurator.js';
const CUSTOMER_ID = '019e8e65-f52f-7e7a-aa83-410561f9e42c';
const CONFIGURATOR_ORIGIN = 'https://configurator.v2.londondynamics.com';
const SETTLED_MESSAGE_TYPES = ['ready', 'load', 'error'];

/** @type {Promise<never> | null} */
let scriptFailure = null;

/**
 * Adds the web component script once per page. The promise never resolves: it only
 * rejects if the script fails to load, and the failed tag is dropped so the next call
 * can try again.
 * @returns {Promise<never>}
 */
function loadConfiguratorScript() {
  if (scriptFailure) return scriptFailure;

  scriptFailure = new Promise((_resolve, reject) => {
    /** @type {HTMLScriptElement | null} */
    const existingScript = document.querySelector('script[data-london-dynamics-configurator]');
    const script = existingScript || document.createElement('script');

    if (!existingScript) {
      script.type = 'module';
      script.src = SCRIPT_URL;
      script.dataset.londonDynamicsConfigurator = '';
    }

    script.addEventListener(
      'error',
      () => {
        script.remove();
        scriptFailure = null;
        reject(new Error('Configurator script failed to load'));
      },
      { once: true }
    );

    if (!existingScript) document.head.appendChild(script);
  });

  return scriptFailure;
}

/**
 * ld-configurator re-emits every message from its origin, whichever iframe sent it,
 * so with two configurators on the page one would hear the other's "ready". Match
 * the sender against this element's own iframe instead.
 * @param {HTMLElement} configurator
 * @param {(data: { type: string, values?: unknown }) => void} callback
 * @returns {() => void} unsubscribe
 */
function onConfiguratorSettled(configurator, callback) {
  /** @param {MessageEvent} event */
  const handleMessage = (event) => {
    if (event.origin !== CONFIGURATOR_ORIGIN) return;
    if (!SETTLED_MESSAGE_TYPES.includes(event.data?.type)) return;

    const frame = configurator.shadowRoot?.querySelector('iframe');
    if (!frame || event.source !== frame.contentWindow) return;

    window.removeEventListener('message', handleMessage);
    callback(event.data);
  };

  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
}

/**
 * Appends an ld-configurator for `sku` to `host` and loads the web component script.
 *
 * `onSettled` gets the iframe's first ready/load/error message: getCurrentConfiguration()
 * has no timeout and can hang before the iframe is up, so wait for this instead.
 * `onError` gets a script load failure; the element is already removed by then.
 *
 * @param {HTMLElement} host
 * @param {object} options
 * @param {string} options.sku
 * @param {Record<string, string>} [options.attributes] Extra attributes for the element.
 * @param {(data: { type: string, values?: unknown }) => void} [options.onSettled]
 * @param {(error: Error) => void} [options.onError]
 * @returns {HTMLElement}
 */
export function mountConfigurator(host, { sku, attributes = {}, onSettled, onError }) {
  const scriptLoad = loadConfiguratorScript();

  const configurator = document.createElement('ld-configurator');
  configurator.setAttribute('style', 'display:block; width:100%; height:100%;');
  configurator.setAttribute('customer-id', CUSTOMER_ID);
  configurator.setAttribute('sku', sku);
  Object.entries(attributes).forEach(([name, value]) => configurator.setAttribute(name, value));

  const unsubscribe = onConfiguratorSettled(configurator, (data) => onSettled?.(data));

  host.appendChild(configurator);

  scriptLoad.catch((error) => {
    unsubscribe();
    configurator.remove();
    onError?.(error);
  });

  return configurator;
}
